import { campaignPreRegisterAPI } from 'api/campaign'
import { Status } from 'api/common/statuses'
import Footer from 'components/Footer'
import Header from 'components/Header'
import * as React from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ROUTE_CAMPAIGN_SIGN_UP_COMPLETE, ROUTE_LOGIN, ROUTE_REGISTRATION_EXPIRED, buildCampaignSignUpUrl } from 'constants/routes'
import type { CampaignSignUpFormValues } from 'scenes/CampaignSignUp'

const PASSWORD_MASK = '••••••••' as const
const LOGIN_PATH = '/login' as const
const LOGIN_BUTTON_LABEL = 'ログイン画面へ' as const
const FALLBACK_REGISTER_ERROR_MESSAGE = '登録処理でエラーが発生しました。時間をおいて再度お試しください。' as const

const STATUS_TO_MESSAGE: Partial<Record<Status, string>> = {
  [Status.AlreadyRegistered]: 'このメールアドレスは既に登録されています。ログインして本登録にお進みください。',
  [Status.CampaignAlreadyApplied]: '既に申込を受け付けています。メールが届かない場合は約1時間後に再度お試しください。',
  [Status.DuplicateAtemQuestId]: 'クエストIDが既に他のユーザーで利用されています。',
  [Status.ValidateError]: '入力内容に誤りがあります。修正画面で入力内容（特にパスワードは半角英数字・大文字小文字・数字を含む 8 文字以上）をご確認ください。',
}

const ConfirmRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="mt-3">
    <div className="fw-bold">{label}</div>
    <div className="mt-1">{value}</div>
  </div>
)

type LocationState = { formValues?: CampaignSignUpFormValues }

const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { hash } = useParams()
  const formValues = ((location.state ?? {}) as LocationState).formValues

  const [submitting, setSubmitting] = React.useState<boolean>(false)
  const [errorMessage, setErrorMessage] = React.useState<string>('')
  const [errorStatus, setErrorStatus] = React.useState<Status | null>(null)

  React.useEffect(() => {
    // Rev.20 S46: SignUpRegisterTypeSelect 廃止に伴い、state 欠落時は /login にフォールバック
    if (!formValues || !hash) {
      navigate(ROUTE_LOGIN)
    }
  }, [formValues, hash, navigate])

  if (!formValues || !hash) {
    return null
  }

  const handleEdit = (): void => {
    navigate(buildCampaignSignUpUrl(hash), { state: { formValues } })
  }

  const handleSubmit = async (): Promise<void> => {
    setSubmitting(true)
    setErrorMessage('')
    setErrorStatus(null)
    await campaignPreRegisterAPI(
      {
        email: formValues.email,
        password: formValues.password,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        birthday: formValues.birthday,
        atemQuestId: formValues.atemQuestId,
        hash,
        // Rev.20 S46: 概要書面交付方法選択結果を BE に転送
        // (formValues.summaryDocumentKind は概要書面画面で必ず設定済の前提、validator が enum 検証)
        summaryDocumentKind: formValues.summaryDocumentKind!,
        summaryDocumentNumbers: formValues.summaryDocumentNumbers,
      },
      (status) => {
        setSubmitting(false)
        if (status === Status.Success) {
          navigate(ROUTE_CAMPAIGN_SIGN_UP_COMPLETE)
          return
        }
        if (status === Status.CampaignQrExpired || status === Status.CampaignQrInvalid) {
          navigate(ROUTE_REGISTRATION_EXPIRED)
          return
        }
        setErrorMessage(STATUS_TO_MESSAGE[status] ?? FALLBACK_REGISTER_ERROR_MESSAGE)
        setErrorStatus(status)
      },
    )
  }

  return (
    <>
      <Header title="仮登録申込内容の確認" />
      <main>
        <section className="mx-4 my-5">
          <ConfirmRow label="メールアドレス" value={formValues.email} />
          <ConfirmRow label="氏名" value={`${formValues.lastName} ${formValues.firstName}`} />
          <ConfirmRow label="生年月日" value={formValues.birthday} />
          <ConfirmRow label="ログインパスワード" value={PASSWORD_MASK} />
          <ConfirmRow label="クエストID" value={formValues.atemQuestId} />

          {errorMessage && <p className="note text-danger mt-4">{errorMessage}</p>}
          {errorStatus === Status.AlreadyRegistered && (
            <Button variant="outline-primary" className="w-100 py-3 mt-3" href={LOGIN_PATH}>
              {LOGIN_BUTTON_LABEL}
            </Button>
          )}

          <div className="mt-5 d-grid gap-3">
            <Button variant="primary" className="py-4" onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Spinner size="sm" /> : '送信する'}
            </Button>
            <Button variant="outline-secondary" className="py-4" onClick={handleEdit} disabled={submitting}>
              修正する
            </Button>
          </div>
        </section>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent
